import { useCallback, useMemo } from 'react';
import { apiClient } from '../lib/apiClient';
import { useToken } from '../utils/token';
import useDataDog from './DataDog';
import { TrackEventNames } from './trackEventNames';
import { C2foPayload, ClientMeta, ReportAttributes } from './types';

async function postTrack(payload: C2foPayload): Promise<void> {
  if (import.meta.env.MODE === 'development') {
    console.log('track event', { [payload.eventName]: payload });
  }

  if (import.meta.env.VITE_ENABLE_ANALYTICS === 'true') {
    await apiClient().post('api/analytics/track', { json: payload });
  }
}

export function useReporting() {
  const { tokenContent } = useToken();
  const authServiceUuid = tokenContent?.payload?.user?.uuid ?? null;
  const sessionUuid = tokenContent?.payload?.user?.sessionUuid ?? null;
  const { addAction: dataDogAddAction } = useDataDog();
  const uri = typeof window !== 'undefined' ? window.location.href : '';
  const uriQueryParams = typeof window !== 'undefined' ? window.location.search : '';
  const uriStateName = typeof window !== 'undefined' ? window.location.pathname : '';
  const clientMeta = useMemo<ClientMeta>(
    () => ({
      createdDateTime: new Date(),
      sessionUuid,
      uriStateName,
      uri,
      uriQueryParams,
    }),
    [sessionUuid, uri, uriQueryParams, uriStateName],
  );

  const track = useCallback(
    async (event: TrackEventNames, attributes?: ReportAttributes) => {
      const appId = import.meta.env.VITE_APP_NAME ?? 'admin-ui';

      if (authServiceUuid) {
        const namespacedEvent = `${appId}::${event}`;

        const properties: ReportAttributes = {
          clientMeta,
          ...(attributes && { eventMeta: attributes }),
        };

        const payload: C2foPayload = {
          appId,
          authServiceUuid,
          eventName: namespacedEvent,
          properties,
          sendToAtlasQueue: true,
          sessionUuid: clientMeta.sessionUuid,
        };

        try {
          dataDogAddAction(namespacedEvent, { ...properties });
          await postTrack({ ...payload, properties: { ...properties, sessionUuid: clientMeta.sessionUuid } });
        } catch {
          // Don't block app functionality if tracking fails
        }
      }
    },
    [authServiceUuid, clientMeta, dataDogAddAction],
  );

  return { track };
}
